import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  ArrowRight,
  Search,
  ClipboardList,
  Truck,
  BellRing,
  Video,
  MapPinned,
  QrCode,
  Smartphone,
  Monitor,
  Database,
  Radio,
  Megaphone,
  Handshake,
  TrendingUp,
  Palette,
  Globe2,
  BedDouble,
  ArrowUpRight,
  Sparkles,
  Search as SearchIcon,
  Wrench,
  Rocket,
  LineChart,
  ChevronDown,
  Coffee,
} from "lucide-react";
import MaskedReveal from "@/components/motion/MaskedReveal";
import Reveal from "@/components/motion/Reveal";
import HexMark from "@/components/motion/HexMark";
import { IMAGES } from "@/constants/site";

const chapters = [
  {
    id: "01",
    kicker: "Chapter 01 · Flagship Service",
    title: "Full-Service Strategic Meeting, Event & Association Management",
    lede:
      "Our premier engagement. Full-service concierge or hourly retainer for meetings, associations, and productions of any scale — sourced, planned, and executed as one accountable agency.",
    img: IMAGES.strategic,
    featured: true,
    services: [
      { icon: Search, name: "Sourcing", body: "Venues, vendors and destinations negotiated against your KPIs.", to: "/services/site-selection" },
      { icon: ClipboardList, name: "Project Management", body: "Certified PMs owning timeline, risk and execution.", to: "/services/project-management" },
      { icon: Truck, name: "Logistics Planning", body: "Site recon to strike — the full operating dossier.", to: "/services/logistics-planning" },
      { icon: BedDouble, name: "Hotel & Travel Accommodations", body: "IATAN-accredited group housing, air, and ground ops.", to: "/services/hotel-travel" },
    ],
    extras: [
      { icon: BellRing, name: "Concierge Solutions", body: "Hourly, day-of or fully embedded — your call." },
      { icon: Video, name: "Audio-Visual Productions", body: "Full technical direction for stages and broadcasts." },
      { icon: MapPinned, name: "On-Site Execution & Travel Directors", body: "Certified travel directors and floor teams." },
    ],
  },
  {
    id: "02",
    kicker: "Chapter 02 · Technology Solution",
    title: "Event Tech Concierge",
    lede:
      "Registration, mobile, hybrid broadcast, data, and sponsorship — architected as one product so attendees feel connected and engaged.",
    img: IMAGES.tech,
    featured: false,
    services: [
      { icon: QrCode, name: "Registration Platforms", body: "Configured to your journey — badging to check-in." },
      { icon: Smartphone, name: "Mobile App Integration", body: "Native or web, tied to your registration system or attendee community." },
      { icon: Monitor, name: "Onsite Tech PMs", body: "Dedicated technologists in the room, not just Zoom." },
      { icon: Database, name: "Data Entry Management", body: "Clean, structured, exportable — for real analytics." },
      { icon: Radio, name: "Virtual & Hybrid Meetings", body: "Broadcast-grade productions with global reach." },
      { icon: Megaphone, name: "Attendee Outreach", body: "Segmented pre-, mid-, and post-event communications." },
      { icon: Handshake, name: "Sponsorship Management", body: "Inventory, activations, delivery, and proof." },
      { icon: TrendingUp, name: "Business Strategy & Performance", body: "Program KPIs tied to revenue and growth." },
      { icon: Palette, name: "Marketing, Social & Branding", body: "Complete branding & marketing strategies with proven growth techniques." },
      { icon: Globe2, name: "Web Design & AI Custom Solutions", body: "Bespoke digital solutions and customized applications for the event." },
    ],
  },
];

function ServiceIconCard({ Icon, name, body, index, to, dark = false }) {
  const numeral = `/${String(index + 1).padStart(2, "0")}`;
  const commonInner = (
    <>
      <div className="icon-tile mb-5">
        <Icon size={20} />
      </div>
      <div className="flex items-baseline gap-2 mb-1">
        <span
          className={`font-mono-brand text-[10px] uppercase tracking-[0.22em] ${
            dark ? "text-cyan-300/70" : "text-[var(--text-dim)]"
          }`}
        >
          {numeral}
        </span>
      </div>
      <h3
        className={`font-editorial text-xl md:text-[22px] leading-tight ${
          dark ? "text-white" : "text-[var(--text)]"
        }`}
      >
        {name}
      </h3>
      <p
        className={`mt-3 text-[13px] leading-relaxed ${
          dark ? "text-white/60" : "text-[var(--text-muted)]"
        }`}
      >
        {body}
      </p>
      {to && (
        <span
          className={`mt-5 inline-flex items-center gap-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] ${
            dark
              ? "text-cyan-300 group-hover:text-white"
              : "text-[var(--accent)] group-hover:text-[var(--text)]"
          } transition-colors`}
        >
          Explore <ArrowUpRight size={12} />
        </span>
      )}
      {to && (
        <ArrowRight
          size={14}
          className={`absolute top-6 right-6 transition-all ${
            dark
              ? "text-white/30 group-hover:text-cyan-300"
              : "text-[var(--text-dim)] group-hover:text-[var(--accent)]"
          } group-hover:translate-x-0.5`}
        />
      )}
    </>
  );

  const baseClass = dark
    ? "group relative bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-cyan-400/40 rounded-2xl p-6 transition-all duration-500 h-full"
    : "group relative bg-white border border-[var(--border)] rounded-2xl p-6 hover:border-[var(--accent)]/45 hover:shadow-[0_16px_40px_-18px_rgba(6,182,212,0.4)] hover:-translate-y-0.5 transition-all duration-500 h-full";

  if (to) {
    return (
      <Link
        to={to}
        data-testid={`service-card-link-${to.split("/").pop()}`}
        className={`${baseClass} block`}
      >
        {commonInner}
      </Link>
    );
  }
  return <div className={baseClass}>{commonInner}</div>;
}

export default function Services() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  return (
    <div className="bg-white text-[var(--text)]">
      <Seo
        title="Event & Meeting Services — Strategic Management, Event Tech, VIP & Site Selection"
        description="Two flagship services under one roof: full-service Strategic Meeting Management and modern Event Tech Concierge (registration, mobile, hybrid broadcast, data). Certified project management for corporate meetings, associations, and productions."
        path="/services"
        keywords="event management services, corporate meeting management, association meeting planning, event technology, event registration, hybrid event production, site selection, VIP travel management"
      />
      {/* HEADER */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-50 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[500px] h-[500px] -top-20 -right-20 opacity-40" />
        <HexMark
          size={260}
          opacity={0.06}
          className="absolute -left-16 top-32 rotate-[12deg] pointer-events-none hidden md:block"
        />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <span className="chip">
              <span className="chip-dot" /> Services
            </span>
            <MaskedReveal
              lines={["Every element", "executed with", "no surprises."]}
              className="mt-6 font-editorial tracking-[-0.03em] leading-[0.95] text-[12vw] md:text-[7.8vw] lg:text-[6.4vw]"
              stagger={0.1}
            />
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <Reveal delay={0.4}>
              <p className="text-[var(--text-muted)] text-lg leading-relaxed">
                Solid event &amp; meeting strategies in one solution. Retain us for a
                single scope, or include us as your full concierge solution. Hourly
                or full-service — always accountable.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CHAPTER 01 — Flagship (light) */}
      <section id="chapter-01" className="relative py-20 md:py-28 border-b border-[var(--border)] overflow-hidden scroll-mt-28">
        <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] top-40 -left-40 opacity-40" />
        <div className="brand-bloom brand-bloom-navy w-[420px] h-[420px] bottom-0 right-0 opacity-25" />
        <HexMark
          size={230}
          opacity={0.05}
          className="absolute right-6 -top-8 rotate-[20deg] pointer-events-none hidden lg:block"
        />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--accent)]">
                    {chapters[0].kicker}
                  </span>
                  <span className="chip">
                    <span className="chip-dot" /> Premier · Flagship
                  </span>
                </div>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl text-balance">
                  {chapters[0].title}
                </h2>
              </div>
              <div className="max-w-sm">
                <p className="text-[var(--text-muted)] leading-relaxed">
                  {chapters[0].lede}
                </p>
              </div>
            </div>
          </Reveal>

          {/* Hero image band for chapter */}
          <Reveal delay={0.1}>
            <div className="relative aspect-[16/10] md:aspect-[21/9] overflow-hidden rounded-2xl mb-8">
              <img
                src={chapters[0].img}
                alt={chapters[0].title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#050A14]/60 via-transparent to-[#050A14]/40" />
              <div className="absolute top-4 left-4 md:top-5 md:left-5">
                <span className="chip !bg-white/90 !text-[var(--text)] !border-white/50">
                  /ref · 01
                </span>
              </div>
              <div className="absolute bottom-5 left-5 right-5 md:bottom-6 md:left-6 md:right-6">
                <p className="font-editorial text-white text-2xl sm:text-3xl md:text-5xl leading-tight tracking-tight max-w-2xl">
                  Concierge services.{" "}
                  <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent italic font-medium">
                    Complete logistics management.
                  </span>
                </p>
              </div>
            </div>
          </Reveal>

          {/* Rich icon cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {chapters[0].services.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.06}>
                <ServiceIconCard Icon={s.icon} name={s.name} body={s.body} to={s.to} index={i} />
              </Reveal>
            ))}
          </div>

          {/* "Also included" — combined card for services without dedicated pages */}
          {chapters[0].extras?.length > 0 && (
            <Reveal delay={0.25}>
              <div
                data-testid="ch01-also-included"
                className="mt-6 md:mt-8 relative rounded-2xl border border-[var(--border)] bg-gradient-to-br from-white via-[var(--surface)] to-white overflow-hidden"
              >
                <span className="corner-cross" style={{ top: 14, right: 14 }} />
                <span className="corner-cross" style={{ bottom: 14, left: 14 }} />
                <div className="absolute -top-24 -right-24 w-[280px] h-[280px] rounded-full bg-cyan-500/8 blur-3xl pointer-events-none" />

                <div className="relative grid grid-cols-12 gap-6 md:gap-10 p-6 md:p-10">
                  {/* Left: header */}
                  <div className="col-span-12 md:col-span-4 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                        Also included
                      </span>
                      <span className="h-px w-8 bg-[var(--border)]" />
                    </div>
                    <h3 className="font-editorial text-2xl md:text-3xl leading-[1.05] tracking-[-0.02em]">
                      Everything else your{" "}
                      <span className="italic text-cyan-600 font-medium">
                        program needs.
                      </span>
                    </h3>
                    <p className="mt-4 text-[var(--text-muted)] text-[14px] leading-relaxed max-w-xs">
                      Rolled into every Chapter A engagement — no separate SOW,
                      no add-on line items.
                    </p>
                  </div>

                  {/* Right: 4-column mini grid (3 services + "and more") */}
                  <div className="col-span-12 md:col-span-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                      {chapters[0].extras.map((s) => (
                        <div
                          key={s.name}
                          data-testid={`ch01-extra-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="group relative bg-white rounded-xl border border-[var(--border)] p-5 h-full hover:border-[var(--accent)]/40 hover:shadow-[0_12px_30px_-16px_rgba(6,182,212,0.35)] transition-all duration-500"
                        >
                          <div className="icon-tile !w-10 !h-10 mb-4">
                            <s.icon size={16} />
                          </div>
                          <h4 className="font-editorial text-[15.5px] leading-tight text-[var(--text)]">
                            {s.name}
                          </h4>
                          <p className="mt-2 text-[12.5px] text-[var(--text-muted)] leading-relaxed">
                            {s.body}
                          </p>
                        </div>
                      ))}

                      {/* …and More — signals additional services beyond the list */}
                      <Link
                        to="/contact"
                        data-testid="ch01-and-more"
                        className="group relative rounded-xl border border-dashed border-[var(--accent)]/40 bg-gradient-to-br from-cyan-500/[0.04] to-sky-500/[0.06] p-5 h-full flex flex-col justify-between hover:border-[var(--accent)] hover:bg-cyan-500/[0.08] transition-all duration-500 overflow-hidden"
                      >
                        <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-cyan-500/10 blur-2xl group-hover:bg-cyan-500/20 transition-colors" />
                        <div className="relative">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-sky-600 text-white flex items-center justify-center shadow-md mb-4">
                            <Sparkles size={16} />
                          </div>
                          <h4 className="font-editorial text-[18px] md:text-[20px] leading-tight text-[var(--text)]">
                            …and more!
                          </h4>
                          <p className="mt-2 text-[12.5px] text-[var(--text-muted)] leading-relaxed">
                            Custom add-ons scoped to your program. Ask us.
                          </p>
                        </div>
                        <span className="relative inline-flex items-center gap-2 mt-4 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] group-hover:translate-x-0.5 transition-transform">
                          Let&rsquo;s talk <ArrowUpRight size={12} />
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* APPROACH — Strategic Planning (moved from About/Portfolio) */}
      <section className="relative py-24 md:py-32 border-t border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[500px] h-[500px] -top-32 -right-20 opacity-40" />
        <div className="brand-bloom brand-bloom-cyan w-[420px] h-[420px] bottom-0 -left-32 opacity-30" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="chip">
              <span className="chip-dot" /> Our Approach
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-[-0.03em] max-w-4xl text-balance">
              Partner with a professional so you can{" "}
              <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                enjoy the experience.
              </span>
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-12 gap-8 md:gap-12">
            <Reveal delay={0.1} className="col-span-12 md:col-span-6">
              <p className="text-[var(--text)] text-lg leading-relaxed">
                Strategic planning for event management involves a systematic
                approach to organizing and executing events that align with an
                organization&apos;s objectives.
              </p>
              <p className="mt-6 text-[var(--text)] text-lg leading-relaxed">
                As certified project management and meeting professionals, you can
                rely on us to support and lead with confidence.
              </p>
            </Reveal>
            <Reveal delay={0.2} className="col-span-12 md:col-span-6">
              <p className="text-[var(--text)] text-lg leading-relaxed">
                Strategic planning for event management involves a systematic
                approach to organizing and executing events that align with an
                organization&apos;s objectives. Clear goals for the event can be
                defined, such as brand promotion, customer engagement, or lead
                generation. By understanding the target audience and market trends,
                the event experience can be tailored effectively, ensuring optimal
                resource allocation across all aspects, including venue, speakers
                and logistics. Finally, a detailed plan will be developed to cover
                all event aspects, from venue selection to speaker engagement,
                promotions, supplier services and logistical details.
              </p>
            </Reveal>
          </div>

          {/* Working Smarter subsection */}
          <Reveal delay={0.3}>
            <div className="mt-20 md:mt-28 card-white p-8 md:p-14 grid grid-cols-12 gap-8 md:gap-10 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-[320px] h-[320px] rounded-full bg-cyan-100/60 blur-3xl pointer-events-none" />
              <div className="col-span-12 md:col-span-5 relative">
                <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--accent)]">
                  Method
                </span>
                <h3 className="mt-5 font-editorial text-3xl md:text-5xl leading-[1] tracking-[-0.025em]">
                  Working smarter
                  <br />
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    to reduce stress.
                  </span>
                </h3>
              </div>
              <div className="col-span-12 md:col-span-7 relative">
                <p className="text-[var(--text)] text-lg leading-relaxed">
                  As you know, planning a meeting or event can be stressful. Our
                  goal is to eliminate most of the stress and anxiety with a good
                  plan of action, proven strategies, and a well-executed team of
                  professionals.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW WE ACTUALLY DO IT — clickable steps + benefits */}
      <section
        className="relative py-20 md:py-28 bg-[var(--surface)] overflow-hidden"
        aria-labelledby="smm-how-we-actually-do-it"
      >
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[420px] h-[420px] -top-20 -left-20 opacity-25" />
        <div className="brand-bloom brand-bloom-blue w-[420px] h-[420px] -bottom-24 -right-24 opacity-25" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Fun logistics-y hero image — the ORG chart at 3 a.m. */}
          <Reveal>
            <div className="relative mb-12 md:mb-16 rounded-3xl overflow-hidden border border-[var(--border)] min-h-[460px] sm:min-h-[420px] md:min-h-0 md:aspect-[16/5]">
              <img
                src="/portfolio/case04-logistics-ballroom.webp"
                alt="Show-day operations run board — sticky notes, radios, and stopwatches"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#050A14]/90 via-[#050A14]/60 md:via-[#050A14]/40 to-[#050A14]/30 md:to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050A14]/60 md:from-[#050A14]/40 to-transparent" />

              <div className="relative min-h-[460px] sm:min-h-[420px] md:min-h-0 md:h-full flex items-center px-6 py-10 md:px-14 md:py-0">
                <div className="max-w-2xl text-white">
                  <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25 backdrop-blur">
                    <Sparkles size={12} /> The messy middle
                  </span>
                  <h2
                    id="smm-how-we-actually-do-it"
                    className="mt-5 font-editorial text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.03em] text-balance"
                  >
                    Behind every calm ballroom{" "}
                    <span className="italic font-medium bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">
                      is a very busy planner.
                    </span>
                  </h2>
                  <p className="mt-4 text-white/80 max-w-lg text-sm sm:text-base leading-relaxed">
                    Here&rsquo;s how we actually run a Strategic Meeting
                    Management engagement — and why our team keeps getting
                    invited back.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Header row above the accordions */}
          <Reveal>
            <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-8 md:mb-12">
              <div className="col-span-12 lg:col-span-8">
                <span className="chip"><span className="chip-dot" /> How we actually do it</span>
                <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed max-w-2xl">
                  Four honest phases. Click any card below to see how we run it —
                  plus the <span className="text-[var(--accent)] font-medium">Why we&rsquo;re a great fit</span> for that phase.
                </p>
              </div>
              <div className="col-span-12 lg:col-span-4 lg:pl-4">
                <p className="font-mono-brand text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]">
                  ↓ tap the cards
                </p>
              </div>
            </div>
          </Reveal>

          {/* Timeline connector */}
          <div className="relative">
            <div
              aria-hidden="true"
              className="hidden lg:block absolute top-[52px] left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-300/50 to-transparent"
            />

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-5">
              {[
                {
                  n: "01",
                  icon: SearchIcon,
                  t: "Discover",
                  vibe: "The 'listen first, sell later' phase.",
                  d: "We start with questions, not a sales pitch. Goals, audiences, KPIs, and the realities nobody wants to say out loud.",
                  how: [
                    "Explore event objectives and requirements",
                    "Working session with your program owner + stakeholders",
                    "Audience, KPI, and budget alignment",
                    "A one-page canvas your exec team actually reads",
                  ],
                  benefit:
                    "Programs land on-strategy from day one — no scope creep at week six because we caught the real objective in the room.",
                },
                {
                  n: "02",
                  icon: Wrench,
                  t: "Configure",
                  vibe: "The 'chef's kitchen' phase.",
                  d: "This is where the plan gets stood up — quietly, methodically, and always rehearsed before it touches your attendees.",
                  how: [
                    "Project & logistics planning",
                    "Workflow, brand, and content tuned to your audience",
                    "Vendors, venues, and hotel blocks negotiated",
                    "Full sandbox rehearsal to catch the ugly edges early",
                  ],
                  benefit:
                    "You get a program that's already been road-tested behind the curtain — surprises stay in the rehearsal room, not on show day.",
                },
                {
                  n: "03",
                  icon: Rocket,
                  t: "Launch",
                  vibe: "The 'lights, camera, action' phase.",
                  d: "Show day. Doors open. Nothing breaks — because we already broke it in rehearsal. We stay from load-in to strike.",
                  how: [
                    "Named onsite lead + trained showrunners in-room",
                    "24/7 communications bridge with your team",
                    "Redundancy on every mission-critical layer",
                  ],
                  benefit:
                    "The room stays calm because we're the ones running the choreography — you get to be a leader, not a lifeguard.",
                },
                {
                  n: "04",
                  icon: LineChart,
                  t: "Optimize",
                  vibe: "The 'you're already planning next year' phase.",
                  d: "Post-event isn't a report — it's a decision. We hand you the details that shapes the next event or program.",
                  how: [
                    "Executive dashboard delivered within days, not weeks",
                    "Sponsor, session, and attendee signal you can act on",
                    "Debrief workshop: what to keep, kill, or dial up",
                  ],
                  benefit:
                    "Your next RFP writes itself — because we hand you the data (and the honest opinions) most partners quietly avoid.",
                },
              ].map((s, i) => {
                const StepIcon = s.icon;
                return (
                  <Reveal key={s.n} delay={i * 0.06}>
                    <details
                      className="group relative card-white p-6 md:p-7 h-full rounded-2xl border border-[var(--border)] hover:border-cyan-300/60 open:border-cyan-400/70 open:shadow-[0_20px_50px_-20px_rgba(6,182,212,0.35)] transition-all duration-300 cursor-pointer"
                      data-testid={`smm-how-step-${s.n}`}
                    >
                      <summary className="list-none marker:hidden select-none">
                        <div className="numeral-mono text-5xl md:text-7xl absolute -top-2 right-4 select-none group-open:text-cyan-500/30 transition-colors">
                          {s.n}
                        </div>
                        <div className="flex items-start justify-between gap-3">
                          <div className="icon-tile"><StepIcon size={18} /></div>
                          <ChevronDown
                            size={18}
                            className="mt-2 text-[var(--text-muted)] group-open:rotate-180 group-open:text-[var(--accent)] transition-transform duration-300"
                          />
                        </div>
                        <h3 className="mt-5 font-editorial text-2xl leading-tight">{s.t}</h3>
                        <p className="mt-1 text-[13px] italic text-[var(--accent)]">{s.vibe}</p>
                        <p className="mt-3 text-[var(--text-muted)] text-sm leading-relaxed">{s.d}</p>
                        <span className="mt-4 inline-flex items-center gap-1 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] group-open:hidden">
                          Peek behind the curtain <ChevronDown size={11} />
                        </span>
                      </summary>

                      <div className="mt-5 pt-5 border-t border-[var(--border)] space-y-5">
                        <div>
                          <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text)] mb-3">
                            What happens
                          </p>
                          <ul className="space-y-2.5">
                            {s.how.map((line) => (
                              <li key={line} className="flex items-start gap-2.5 text-[13.5px] leading-relaxed text-[var(--text)]/85">
                                <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-gradient-to-br from-cyan-400 to-sky-600" />
                                <span>{line}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* BENEFIT — why we'd be a great fit */}
                        <div className="rounded-xl bg-gradient-to-br from-cyan-500/10 via-white to-sky-500/10 border border-cyan-300/40 p-4">
                          <p className="flex items-center gap-2 font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--accent)] mb-2">
                            <Sparkles size={11} /> Why we&rsquo;re a great fit
                          </p>
                          <p className="text-[13.5px] leading-relaxed text-[var(--text)]/90">
                            {s.benefit}
                          </p>
                        </div>
                      </div>
                    </details>
                  </Reveal>
                );
              })}
            </div>
          </div>

          {/* Casual coffee closer */}
          <Reveal delay={0.15}>
            <div className="mt-12 md:mt-14 rounded-2xl border border-dashed border-cyan-300/50 bg-white/60 backdrop-blur px-6 py-6 md:px-8 md:py-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
              <div className="flex items-start gap-3">
                <span className="shrink-0 w-10 h-10 rounded-full bg-cyan-50 border border-cyan-200 flex items-center justify-center">
                  <Coffee size={16} className="text-[var(--accent)]" />
                </span>
                <div>
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--accent)]">P.S.</p>
                  <p className="font-editorial text-lg md:text-xl mt-1 leading-snug">
                    Coffee&rsquo;s on us. Bring the messiest program on your desk.
                  </p>
                </div>
              </div>
              <Link to="/contact#book" className="btn-primary shrink-0 w-full md:w-auto justify-center" data-testid="smm-how-final-cta">
                Grab 15 minutes <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CHAPTER 02 — Event Tech · small CTA banner at the end */}
      <section id="chapter-02" className="py-14 md:py-20 scroll-mt-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Link
            to="/services/event-tech"
            data-testid="chapter-02-mini-banner"
            className="group relative block card-dark overflow-hidden rounded-3xl px-6 md:px-10 py-8 md:py-10 hover:shadow-[0_25px_60px_-20px_rgba(6,182,212,0.45)] transition-all duration-500"
          >
            <div className="absolute inset-0 grid-pattern-dark opacity-40 pointer-events-none" />
            <div className="absolute -top-16 -right-16 w-[320px] h-[320px] rounded-full bg-cyan-500/20 blur-3xl" />

            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex-1">
                <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25">
                  <Sparkles size={12} /> {chapters[1].kicker}
                </span>
                <h3 className="mt-4 font-editorial text-2xl md:text-3xl lg:text-4xl leading-tight tracking-[-0.02em] text-white text-balance max-w-2xl">
                  {chapters[1].title}{" "}
                  <span className="italic font-medium bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">
                    — engineered as one solution.
                  </span>
                </h3>
                <p className="mt-3 text-white/60 leading-relaxed max-w-2xl text-[14.5px]">
                  Registration, mobile, hybrid broadcast, data, and sponsorship — a modular technology solution with one accountable partner.
                </p>
              </div>
              <span className="btn-primary shrink-0 group-hover:translate-x-0.5 transition-transform">
                Explore Event Tech <ArrowRight size={14} />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* M.E. DMC — Destination Management Company banner */}
      <section id="me-dmc" className="py-8 md:py-12 scroll-mt-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Link
            to="/services/me-dmc"
            data-testid="me-dmc-mini-banner"
            className="group relative block overflow-hidden rounded-3xl border border-[var(--border)] bg-gradient-to-br from-white via-[var(--surface)] to-white px-6 md:px-10 py-8 md:py-10 hover:shadow-[0_25px_60px_-20px_rgba(6,182,212,0.35)] hover:border-cyan-400/40 transition-all duration-500"
          >
            <div className="absolute inset-0 hex-pattern opacity-60 pointer-events-none" />
            <div className="absolute -top-16 -left-16 w-[280px] h-[280px] rounded-full bg-cyan-500/15 blur-3xl" />
            <div className="absolute -bottom-20 -right-16 w-[320px] h-[320px] rounded-full bg-sky-500/12 blur-3xl" />

            <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="shrink-0 relative">
                <div className="absolute inset-0 rounded-full bg-cyan-400/25 blur-2xl scale-110" />
                <img
                  src="/dmc/logo.png"
                  alt="M.E. DMC — Destination Management Company"
                  className="relative w-24 h-24 md:w-28 md:h-28 object-contain drop-shadow-[0_10px_28px_rgba(6,182,212,0.35)]"
                />
              </div>
              <div className="flex-1">
                <span className="chip">
                  <MapPinned size={12} /> M.E. DMC · Destination Management Company
                </span>
                <h3 className="mt-4 font-editorial text-2xl md:text-3xl lg:text-4xl leading-tight tracking-[-0.02em] text-balance max-w-2xl">
                  Global reach.{" "}
                  <span className="italic font-medium bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent">
                    Local mastery.
                  </span>
                </h3>
                <p className="mt-3 text-[var(--text-muted)] leading-relaxed max-w-2xl text-[14.5px]">
                  Our specialized DMC division handles local logistics,
                  planning, and coordination for corporate meetings,
                  incentives, and special events — in every destination we
                  work.
                </p>
              </div>
              <span className="btn-primary shrink-0 group-hover:translate-x-0.5 transition-transform">
                Explore M.E. DMC <ArrowRight size={14} />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <span className="chip">
              <span className="chip-dot" /> Not sure where to start?
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="mt-8 font-editorial text-5xl md:text-7xl leading-[0.98] tracking-[-0.03em]">
              Tell us the outcome.
              <br />
              <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                We&apos;ll build the plan.
              </span>
            </h3>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/contact" className="btn-primary mt-12">
              Request a Quote <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
